/**
 * Personal security phrase (anti-phishing phrase) rules, mirrored from the
 * backend, which is the source of truth: Unicode NFC normalize, trim, then
 * 3–20 code points made of letters (any script, with their combining marks)
 * and digits, words joined by a single space or hyphen, and at least one letter.
 */
export const SECURITY_PHRASE_MIN_LENGTH = 3;
export const SECURITY_PHRASE_MAX_LENGTH = 20;

// Built with the RegExp constructor so the unicode property escapes are not
// down-levelled or rejected for the es2015 compile target. Each word starts
// with a letter or digit; combining marks (Thai vowels, Indic viramas, ...)
// may follow it.
const SECURITY_PHRASE_PATTERN = new RegExp(
  '^[\\p{L}\\p{N}][\\p{L}\\p{M}\\p{N}]*([ -][\\p{L}\\p{N}][\\p{L}\\p{M}\\p{N}]*)*$',
  'u'
);
const HAS_LETTER_PATTERN = new RegExp('\\p{L}', 'u');

/** Short, user-facing statement of the rule, for hints and descriptions. */
export const SECURITY_PHRASE_RULE_HINT = `${SECURITY_PHRASE_MIN_LENGTH}–${SECURITY_PHRASE_MAX_LENGTH} letters or numbers, at least one letter`;

export const SECURITY_PHRASE_REQUIRED_ERROR = 'Security phrase is required';
export const SECURITY_PHRASE_LENGTH_ERROR = `Use ${SECURITY_PHRASE_RULE_HINT}`;
export const SECURITY_PHRASE_FORMAT_ERROR =
  'Use letters and numbers only, joined by single spaces or hyphens';
export const SECURITY_PHRASE_LETTER_ERROR =
  'Include at least one letter, not only numbers';
export const SECURITY_PHRASE_PERSONAL_ERROR =
  "Don't use your name, phone number or email as your security phrase.";

export interface PhrasePersonalInfo {
  name?: string | null;
  phoneNumber?: string | null;
  countryCode?: string | null;
  email?: string | null;
}

/** Comparison key: NFC, lower-cased, with spaces and hyphens removed. */
const phraseKey = (value: string): string =>
  value
    .normalize('NFC')
    .toLowerCase()
    .replace(/[\s-]+/g, '');

const digitsOf = (value: string | null | undefined): string =>
  (value ?? '').replace(/\D/g, '');

/**
 * True when the phrase is, ignoring case, spaces and hyphens, exactly the
 * user's full name, one word of the name, the phone number (with or without
 * country code) or the part of the email before the `@`. Equality only: a
 * phrase that merely contains one of these is allowed. Mirrors the backend.
 */
export function phraseMatchesPersonalInfo(
  phrase: string,
  info: PhrasePersonalInfo
): boolean {
  const key = phraseKey(phrase);
  if (key === '') return false;

  const forbidden = new Set<string>();
  const name = info.name ?? '';
  forbidden.add(phraseKey(name));
  name.split(/\s+/).forEach((word) => forbidden.add(phraseKey(word)));

  const phone = digitsOf(info.phoneNumber);
  forbidden.add(phone);
  if (phone !== '') forbidden.add(`${digitsOf(info.countryCode)}${phone}`);

  const email = info.email ?? '';
  const at = email.lastIndexOf('@');
  if (at > 0) forbidden.add(phraseKey(email.slice(0, at)));

  forbidden.delete('');
  return forbidden.has(key);
}

/** NFC-normalizes and trims; this is the value sent to the backend. */
export function normalizeSecurityPhrase(
  value: string | null | undefined
): string {
  return (value ?? '').normalize('NFC').trim();
}

/**
 * Returns an error message for an invalid code, or null when valid.
 * An empty value is valid only when `required` is false.
 */
export function validateSecurityPhrase(
  value: string | null | undefined,
  { required, personal }: { required: boolean; personal?: PhrasePersonalInfo }
): string | null {
  const code = normalizeSecurityPhrase(value);
  if (code.length === 0)
    return required ? SECURITY_PHRASE_REQUIRED_ERROR : null;
  const length = Array.from(code).length;
  if (
    length < SECURITY_PHRASE_MIN_LENGTH ||
    length > SECURITY_PHRASE_MAX_LENGTH
  ) {
    return SECURITY_PHRASE_LENGTH_ERROR;
  }
  if (!SECURITY_PHRASE_PATTERN.test(code)) return SECURITY_PHRASE_FORMAT_ERROR;
  if (!HAS_LETTER_PATTERN.test(code)) return SECURITY_PHRASE_LETTER_ERROR;
  if (personal && phraseMatchesPersonalInfo(code, personal)) {
    return SECURITY_PHRASE_PERSONAL_ERROR;
  }
  return null;
}

/** The signed-in user's details the phrase must not repeat. */
export function personalInfoOf(
  user:
    | {
        user_name?: string | null;
        user_phone_number?: string | null;
        country_code?: string | null;
        user_email?: string | null;
      }
    | null
    | undefined
): PhrasePersonalInfo {
  return {
    name: user?.user_name,
    phoneNumber: user?.user_phone_number,
    countryCode: user?.country_code,
    email: user?.user_email
  };
}

const SUGGESTION_ADJECTIVES = [
  'blue',
  'green',
  'golden',
  'silver',
  'happy',
  'quiet',
  'brave',
  'sunny',
  'lucky',
  'swift',
  'calm',
  'bright',
  'cosy',
  'jolly',
  'misty',
  'royal'
];
const SUGGESTION_NOUNS = [
  'tiger',
  'mango',
  'river',
  'falcon',
  'lotus',
  'durian',
  'panda',
  'comet',
  'island',
  'orchid',
  'otter',
  'pepper',
  'rocket',
  'monsoon',
  'lantern',
  'koi'
];

const pick = <T>(items: readonly T[]): T =>
  items[Math.floor(Math.random() * items.length)];

/**
 * A random, easy-to-remember phrase such as `blue-tiger-42`. Always valid:
 * the longest combination is 17 characters.
 */
export function suggestSecurityPhrase(): string {
  const number = 10 + Math.floor(Math.random() * 90);
  return `${pick(SUGGESTION_ADJECTIVES)}-${pick(SUGGESTION_NOUNS)}-${number}`;
}
